import type {FetchError} from 'ofetch';
import {useLogger, createError} from '#imports';
import type {TJoySnackbarLevels} from '@maltjoy/core-vue';
import {pushVJoySnackbar} from '@maltjoy/core-vue';

export interface ErrorOptions {
    level?: TJoySnackbarLevels;
    showSnackbar?: boolean;
}

export interface FetchErrorOptions extends ErrorOptions {
    customErrorMessage?: string;
}

/**
 * Create an error handler
 * It allows to call some function to quickly handle some errors, by logging them and display an error Snackbar from Joy
 *
 * @usage
 *
 * ```ts
 *  const errorHandler = useErrorHandler();
 *  errorHandler.handleError('Error clicking on this button'); // It will log the error and show a snackbar with this message
 * ```
 */
export function useErrorHandler() {
    const logger = useLogger();

    /**
     *  Will log the errorMessage and display an error Snackbar
     *
     * @param errorMessage The error message to log and to show in the snackbar
     * @param options { level : TJoySnackbarLevels, showSnackbar : boolean }
     */
    function handleError(errorMessage: string, {level = 'error', showSnackbar = true}: ErrorOptions = {showSnackbar: true, level: 'error'}): void {
        logger.error(errorMessage);
        if (showSnackbar && import.meta.client) {
            pushVJoySnackbar({props: {message: errorMessage, level: level || 'error'}});
        }
    }

    /**
     *  Handle a FetchError from the API to display the message. It will be logged and a Snackbar will be displayed
     *
     * @param error The FetchError object, directly from the API generated by openAPI
     * @param options { level : TJoySnackbarLevels, showSnackbar : boolean, customErrorMessage: string }
     */
    function handleFetchError(
        error: FetchError,
        {customErrorMessage, level = 'error', showSnackbar = true}: FetchErrorOptions = {showSnackbar: true, level: 'error'},
    ): void {
        const errorMessage = customErrorMessage || (error.message ?? error);
        handleError(errorMessage, {level, showSnackbar});
    }

    /**
     *  Show the full screen Nuxt page error. The nature of the error depends on the statusCode provided
     *
     * @param statusCode The statusCode of the error. It will show a different page.
     * @param errorMessage An optional message that will be displayed
     */
    function handleFatalError(errorMessage: string, statusCode: number): void {
        logger.error(errorMessage);
        throw createError({message: errorMessage, statusCode, fatal: true});
    }

    /**
     * Provide the correct error handler for any type of error
     *
     * @param error the error Object
     * @param options Options combining both FetchErrorOptions and ErrorOptions
     */
    function handleUnknownError(
        error: unknown,
        {customErrorMessage, ...commonOptions}: FetchErrorOptions & ErrorOptions = {showSnackbar: true, level: 'error'},
    ) {
        if (error instanceof Error && error.name === 'FetchError') {
            handleFetchError(error, {customErrorMessage, ...commonOptions});
        } else if (typeof error === 'string') {
            handleError(error, commonOptions);
        } else {
            logger.error(error);
        }
    }

    return {
        handleError,
        handleFetchError,
        handleFatalError,
        handleUnknownError,
    };
}
